import { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, onSnapshot, Timestamp, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Post } from '../types/social';
import {
  getPosts,
  getUserPosts,
  createPost,
  deletePost,
  likePost,
  unlikePost,
  checkUserLikedPost,
  addComment,
  getPostComments,
  deleteComment,
  favoritePost,
  unfavoritePost,
  checkUserFavoritedPost,
} from '../services/socialService';
import { useAuth } from '../contexts/AuthContext';

interface UseSocialReturn {
  posts: Post[];
  isLoading: boolean;
  error: string | null;
  feedMode: 'personalized' | 'explore';
  setFeedMode: (mode: 'personalized' | 'explore') => void;
  refreshPosts: () => Promise<void>;
  createNewPost: (content: string, imageUrl?: string, type?: 'text' | 'image' | 'event' | 'recipe', eventId?: string, eventName?: string, recipeId?: string, recipeName?: string) => Promise<void>;
  removePost: (postId: string) => Promise<void>;
  toggleLike: (postId: string, isLiked: boolean) => Promise<void>;
  checkLike: (postId: string) => Promise<boolean>;
  toggleFavorite: (postId: string, isFavorited: boolean) => Promise<void>;
  checkFavorite: (postId: string) => Promise<boolean>;
  addPostComment: (postId: string, content: string) => Promise<void>;
  getComments: (postId: string) => Promise<any[]>;
  removeComment: (postId: string, commentId: string) => Promise<void>;
}

export const useSocial = (userId?: string, limitCount: number = 20): UseSocialReturn => {
  const { user, hostlyUser } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedMode, setFeedMode] = useState<'personalized' | 'explore'>('personalized');

  const refreshPosts = async () => {
    if (!user?.uid) return;

    try {
      setIsLoading(true);
      setError(null);

      const fetchedPosts = userId
        ? await getUserPosts(userId)
        : await getPosts(limitCount);

      setPosts(fetchedPosts);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar posts');
      console.error('Erro ao carregar posts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewPost = async (
    content: string,
    imageUrl?: string,
    type: 'text' | 'image' | 'event' | 'recipe' = 'text',
    eventId?: string,
    eventName?: string,
    recipeId?: string,
    recipeName?: string
  ) => {
    if (!user?.uid || !hostlyUser?.name) {
      throw new Error('Usuário não autenticado');
    }

    // userName é o identificador único: nickname personalizado ou derivado do email
    const emailUsername = hostlyUser.nickname || hostlyUser.email?.split('@')[0] || hostlyUser.name;
    // Dividir nome completo em primeiro e último nome
    const nameParts = hostlyUser.name.split(' ');
    const firstName = nameParts[0] || hostlyUser.name;
    const lastName = nameParts.slice(1).join(' ') || '';

    try {
      await createPost(
        user.uid,
        emailUsername, // @username derivado do email para identificação única
        firstName,
        lastName,
        hostlyUser.profilePictureUrl,
        {
          content,
          imageUrl,
          type,
          eventId,
          eventName,
          recipeId,
          recipeName,
        }
      );
      await refreshPosts();
    } catch (err: any) {
      console.error('Erro ao criar post:', err);
      throw err;
    }
  };

  const removePost = async (postId: string) => {
    try {
      await deletePost(postId);
      await refreshPosts();
    } catch (err: any) {
      console.error('Erro ao remover post:', err);
      throw err;
    }
  };

  const toggleLike = async (postId: string, isLiked: boolean) => {
    if (!user?.uid || !hostlyUser?.name) {
      throw new Error('Usuário não autenticado');
    }

    const displayName = hostlyUser.name;

    try {
      if (isLiked) {
        await unlikePost(postId, user.uid);
      } else {
        await likePost(postId, user.uid, displayName, hostlyUser.profilePictureUrl);
      }
      await refreshPosts();
    } catch (err: any) {
      console.error('Erro ao dar like:', err);
      throw err;
    }
  };

  const checkLike = async (postId: string): Promise<boolean> => {
    if (!user?.uid) return false;
    return await checkUserLikedPost(postId, user.uid);
  };

  const addPostComment = async (postId: string, content: string) => {
    if (!user?.uid || !hostlyUser?.name) {
      throw new Error('Usuário não autenticado');
    }

    const displayName = hostlyUser.name;

    try {
      await addComment(postId, user.uid, displayName, hostlyUser.profilePictureUrl, content);
      await refreshPosts();
    } catch (err: any) {
      console.error('Erro ao adicionar comentário:', err);
      throw err;
    }
  };

  const getComments = async (postId: string) => {
    try {
      return await getPostComments(postId);
    } catch (err: any) {
      console.error('Erro ao buscar comentários:', err);
      return [];
    }
  };

  const toggleFavorite = async (postId: string, isFavorited: boolean) => {
    if (!user?.uid || !hostlyUser?.name) {
      throw new Error('Usuário não autenticado');
    }

    const displayName = hostlyUser.name;

    try {
      if (isFavorited) {
        await unfavoritePost(postId, user.uid);
      } else {
        await favoritePost(postId, user.uid, displayName, hostlyUser.profilePictureUrl);
      }
      await refreshPosts();
    } catch (err: any) {
      console.error('Erro ao favoritar:', err);
      throw err;
    }
  };

  const checkFavorite = async (postId: string): Promise<boolean> => {
    if (!user?.uid) return false;
    return await checkUserFavoritedPost(postId, user.uid);
  };

  const removeComment = async (postId: string, commentId: string) => {
    try {
      await deleteComment(postId, commentId);
      await refreshPosts();
    } catch (err: any) {
      console.error('Erro ao remover comentário:', err);
      throw err;
    }
  };

  // Listener em tempo real para posts
  useEffect(() => {
    if (!user?.uid) {
      setPosts([]);
      setIsLoading(false);
      return;
    }

    // Se for um perfil específico, não aplicar filtro de feed
    if (userId) {
      const postsRef = collection(db, 'posts');
      const q = query(postsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const fetchedPosts = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
              updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
            } as Post;
          });
          setPosts(fetchedPosts);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Erro no listener de posts:', err);
          setError(err.message);
          setIsLoading(false);
        }
      );

      return () => unsubscribe();
    }

    // Feed personalizado ou explorar
    const loadFeed = async () => {
      try {
        setIsLoading(true);

        if (feedMode === 'explore') {
          // Modo Explorar: todos os posts
          const postsRef = collection(db, 'posts');
          const q = query(postsRef, orderBy('createdAt', 'desc'), limit(limitCount));

          const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
              const fetchedPosts = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
                  updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
                } as Post;
              });
              setPosts(fetchedPosts);
              setIsLoading(false);
              setError(null);
            },
            (err) => {
              console.error('Erro no listener de posts:', err);
              setError(err.message);
              setIsLoading(false);
            }
          );

          return () => unsubscribe();
        } else {
          // Modo Personalizado: posts de quem você segue + seus posts
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const following = userDoc.exists() ? (userDoc.data().following || []) : [];

          // Adicionar o próprio usuário à lista
          const userIdsToShow = [user.uid, ...following];

          if (userIdsToShow.length === 0) {
            setPosts([]);
            setIsLoading(false);
            return;
          }

          const postsRef = collection(db, 'posts');
          const q = query(
            postsRef,
            where('userId', 'in', userIdsToShow.slice(0, 10)), // Firestore limita 'in' a 10 valores
            orderBy('createdAt', 'desc'),
            limit(limitCount)
          );

          const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
              const fetchedPosts = snapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
                  updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
                } as Post;
              });
              setPosts(fetchedPosts);
              setIsLoading(false);
              setError(null);
            },
            (err) => {
              console.error('Erro no listener de posts:', err);
              setError(err.message);
              setIsLoading(false);
            }
          );

          return () => unsubscribe();
        }
      } catch (err: any) {
        console.error('Erro ao carregar feed:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    const cleanup = loadFeed();
    return () => {
      cleanup?.then(unsub => unsub?.());
    };
  }, [user?.uid, limitCount, feedMode, userId]);

  return {
    posts,
    isLoading,
    error,
    feedMode,
    setFeedMode,
    refreshPosts,
    createNewPost,
    removePost,
    toggleLike,
    checkLike,
    toggleFavorite,
    checkFavorite,
    addPostComment,
    getComments,
    removeComment,
  };
};
